import React from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaPlay, FaVideo } from 'react-icons/fa';

const BACKEND_URL = '';

const getPhotoUrl = (photo) => {
  if (photo.file_url) {
    return photo.file_url.startsWith('http') ? photo.file_url : `${BACKEND_URL}${photo.file_url}`;
  }
  return photo.base64_data;
};

const isVideo = (p) => p.media_type === 'video' || (p.content_type && p.content_type.startsWith('video/'));

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

function MediaCell({ photo, onClick }) {
  const vid = isVideo(photo);
  return (
    <div className="aspect-square rounded-lg overflow-hidden cursor-pointer group relative" onClick={onClick}>
      {vid ? (
        <>
          <video src={getPhotoUrl(photo)} className="w-full h-full object-cover" preload="metadata" muted />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow group-hover:scale-110 transition-transform">
              <FaPlay className="text-gray-800 text-xs ml-0.5" />
            </div>
          </div>
          <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
            <FaVideo size={8} />
          </div>
        </>
      ) : (
        <>
          <img src={getPhotoUrl(photo)} alt={photo.caption || 'Photo'} className="w-full h-full object-cover transition-transform group-hover:scale-110" loading="lazy" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none"></div>
        </>
      )}
    </div>
  );
}

function countLabel(photos) {
  const imgs = photos.filter(p => !isVideo(p)).length;
  const vids = photos.filter(p => isVideo(p)).length;
  const parts = [];
  if (imgs > 0) parts.push(`${imgs} photo${imgs !== 1 ? 's' : ''}`);
  if (vids > 0) parts.push(`${vids} video${vids !== 1 ? 's' : ''}`);
  return parts.join(', ') || '0 items';
}

export function TimelineEvent({ event, photos, onPhotoClick, startIndex }) {
  return (
    <div className="relative pl-8 pb-12" data-testid={`timeline-event-${event.id}`}>
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-blue-300"></div>
      <div className="absolute left-0 top-0 w-4 h-4 -translate-x-1/2 rounded-full bg-blue-500 border-4 border-white shadow-md"></div>
      
      <div className="bg-white rounded-xl shadow-lg overflow-hidden ml-4">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold">{event.name}</h3>
              <div className="flex items-center space-x-4 mt-2 text-blue-100 text-sm">
                <div className="flex items-center space-x-1">
                  <FaCalendarAlt />
                  <span>{formatDate(event.date)}</span>
                  {event.endDate && event.endDate !== event.date && <span> - {formatDate(event.endDate)}</span>}
                </div>
                {event.location && (
                  <div className="flex items-center space-x-1"><FaMapMarkerAlt /><span>{event.location}</span></div>
                )}
              </div>
            </div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-sm">{countLabel(photos)}</div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {photos.map((photo, idx) => (
              <MediaCell key={photo.photo_id} photo={photo} onClick={() => onPhotoClick(startIndex + idx)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TimelineOther({ title, icon, photos, onPhotoClick, startIndex }) {
  return (
    <div className="relative pl-8 pb-12">
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 to-purple-300"></div>
      <div className="absolute left-0 top-0 w-4 h-4 -translate-x-1/2 rounded-full bg-purple-500 border-4 border-white shadow-md"></div>
      
      <div className="bg-white rounded-xl shadow-lg overflow-hidden ml-4">
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">{icon}<h3 className="text-xl font-bold">{title}</h3></div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-sm">{countLabel(photos)}</div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {photos.map((photo, idx) => (
              <MediaCell key={photo.photo_id} photo={photo} onClick={() => onPhotoClick(startIndex + idx)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
